import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import type { User } from "@leave-system/shared";
import LeaveTypeManagement from "../components/features/admin/LeaveTypeManagement";
import DepartmentManagement from "../components/features/admin/DepartmentManagement";
import { api } from "../services/api";
import { MainLayout } from "../components/layout";
import { useNavigate } from "../lib/router";

interface AdminStats {
  users: Record<string, number>;
  roles: Record<string, number>;
  departments: number;
}

interface UserWithDetails extends User {
  department?: {
    id: string;
    name: string;
  };
  team?: {
    id: string;
    name: string;
  };
}

interface Department {
  id: string;
  name: string;
  code: string;
  description?: string;
  isActive: boolean;
}

const AdminPanel: React.FC = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>("users");
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [users, setUsers] = useState<UserWithDetails[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    role: "",
    status: "active",
  });
  const [editingUser, setEditingUser] = useState<UserWithDetails | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithDetails | null>(
    null
  );
  const [updatingUsers, setUpdatingUsers] = useState<Set<string>>(new Set());
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] =
    useState<UserWithDetails | null>(null);
  const [resetPasswordForm, setResetPasswordForm] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(
    null
  );
  const [resettingPasswordUserId, setResettingPasswordUserId] = useState<
    string | null
  >(null);

  // Check admin access
  if (!currentUser || currentUser.role !== "admin") {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <h2 className="text-lg font-semibold text-red-800">Access Denied</h2>
        <p className="text-red-600">
          You need admin privileges to access this page.
        </p>
      </div>
    );
  }

  // Fetch admin stats
  const fetchStats = async () => {
    try {
      const response = await api.get("/api/v1/admin/stats");
      const data = response.data;

      if (data.success) {
        setStats(data.data);
      } else {
        throw new Error(data.error?.message || "Failed to fetch stats");
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
      setError("Failed to load admin statistics");
    }
  };

  // Fetch users
  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        ...(filters.search && { search: filters.search }),
        ...(filters.role && { role: filters.role }),
        ...(filters.status && { status: filters.status }),
      });

      const response = await api.get(`/api/v1/admin/users?${params}`);
      const data = response.data;

      if (data.success) {
        setUsers(data.data.users);
        setPagination(data.data.pagination);
      } else {
        throw new Error(data.error?.message || "Failed to fetch users");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setError("Failed to load users");
    }
  };

  // Fetch departments
  const fetchDepartments = async () => {
    try {
      const response = await api.get("/api/v1/departments");
      const data = response.data;

      if (data.success) {
        // Handle both array and object with departments property
        const deptData = Array.isArray(data.data)
          ? data.data
          : data.data.departments || [];
        setDepartments(deptData);
      } else {
        throw new Error(data.error?.message || "Failed to fetch departments");
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  // Update user role
  const updateUserRole = async (userId: string, newRole: string) => {
    if (userId === currentUser?.id && newRole !== "admin") {
      alert("คุณไม่สามารถลบสิทธิ์ admin ของตัวเองได้");
      return;
    }

    const targetUser = users.find((u) => u.id === userId);
    if (!targetUser) return;

    const confirmMessage = `Are you sure you want to change ${targetUser.firstName} ${targetUser.lastName}'s role from ${targetUser.role} to ${newRole}?`;
    if (!confirm(confirmMessage)) return;

    setUpdatingUsers((prev) => new Set(prev).add(userId));

    try {
      const response = await api.patch(`/api/v1/admin/users/${userId}/role`, {
        role: newRole,
      });

      const data = response.data;

      if (data.success) {
        await fetchUsers();
        await fetchStats();
      } else {
        throw new Error(data.error?.message || "Failed to update role");
      }
    } catch (error) {
      console.error("Error updating user role:", error);
      alert("Failed to update user role: " + (error as Error).message);
    } finally {
      setUpdatingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  // Update user status
  const updateUserStatus = async (userId: string, newStatus: string) => {
    if (userId === currentUser?.id && newStatus !== "active") {
      alert("คุณไม่สามารถเปลี่ยนสถานะของตัวเองได้");
      return;
    }

    const targetUser = users.find((u) => u.id === userId);
    if (!targetUser) return;

    const confirmMessage = `Are you sure you want to change ${targetUser.firstName} ${targetUser.lastName}'s status from ${targetUser.status} to ${newStatus}?`;
    if (!confirm(confirmMessage)) return;

    setUpdatingUsers((prev) => new Set(prev).add(userId));

    try {
      const response = await api.patch(`/api/v1/admin/users/${userId}`, {
        status: newStatus,
      });

      const data = response.data;

      if (data.success) {
        await fetchUsers();
        await fetchStats();
      } else {
        throw new Error(data.error?.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating user status:", error);
      alert("Failed to update user status: " + (error as Error).message);
    } finally {
      setUpdatingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  // Update user information
  const updateUserInfo = async (
    userId: string,
    updateData: Partial<UserWithDetails>
  ) => {
    try {
      const response = await api.patch(
        `/api/v1/admin/users/${userId}`,
        updateData
      );

      const data = response.data;

      if (data.success) {
        await fetchUsers();
        setShowEditModal(false);
        setEditingUser(null);
      } else {
        throw new Error(data.error?.message || "Failed to update user");
      }
    } catch (error) {
      console.error("Error updating user:", error);
      alert("Failed to update user: " + (error as Error).message);
    }
  };

  // View user details
  const viewUser = (user: UserWithDetails) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  // Edit user
  const editUser = (user: UserWithDetails) => {
    setEditingUser(user);
    setShowEditModal(true);
  };

  const openResetPasswordModal = (user: UserWithDetails) => {
    setResetPasswordUser(user);
    setResetPasswordForm({
      newPassword: "",
      confirmPassword: "",
    });
    setResetPasswordError(null);
    setShowResetPasswordModal(true);
  };

  const closeResetPasswordModal = () => {
    setShowResetPasswordModal(false);
    setResetPasswordUser(null);
    setResetPasswordForm({
      newPassword: "",
      confirmPassword: "",
    });
    setResetPasswordError(null);
    setResettingPasswordUserId(null);
  };

  const resetUserPassword = async () => {
    if (!resetPasswordUser) return;

    if (!resetPasswordForm.newPassword.trim()) {
      setResetPasswordError("กรุณากำหนดรหัสผ่านใหม่");
      return;
    }

    if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) {
      setResetPasswordError("รหัสผ่านใหม่และการยืนยันรหัสผ่านไม่ตรงกัน");
      return;
    }

    setResetPasswordError(null);
    setResettingPasswordUserId(resetPasswordUser.id);

    try {
      const response = await api.post(
        `/api/v1/admin/users/${resetPasswordUser.id}/reset-password`,
        {
          newPassword: resetPasswordForm.newPassword,
        }
      );

      const data = response.data;

      if (!data.success) {
        throw new Error(data.error?.message || "Failed to reset password");
      }

      alert(
        resetPasswordUser.id === currentUser?.id
          ? "รีเซ็ตรหัสผ่านของบัญชีนี้เรียบร้อยแล้ว และคง session ปัจจุบันไว้ให้"
          : `รีเซ็ตรหัสผ่านของ ${resetPasswordUser.firstName} ${resetPasswordUser.lastName} เรียบร้อยแล้ว ระบบจะบังคับให้ออกจาก session เดิมทั้งหมด`
      );

      closeResetPasswordModal();
    } catch (error) {
      console.error("Error resetting password:", error);
      setResetPasswordError(
        (error as any)?.response?.data?.error?.message ||
          (error as Error).message ||
          "Failed to reset password"
      );
      setResettingPasswordUserId(null);
    }
  };

  const loadUsersTabData = async () => {
    setLoading(true);
    setError(null);
    await Promise.all([fetchStats(), fetchUsers(), fetchDepartments()]);
    setLoading(false);
  };

  useEffect(() => {
    if (activeTab !== "users") {
      setLoading(false);
      setError(null);
      return;
    }

    void loadUsersTabData();
  }, [pagination.page, filters, activeTab]);

  return (
    <MainLayout title="⚙️ Admin Panel">
      <div className="flex gap-6">
        {/* Internal Tab Navigation */}
        <div className="w-64 bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 rounded-lg flex-shrink-0">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-blue-600 rounded-lg">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  System Management
                </h1>
              </div>
            </div>

            {/* Navigation Menu */}
            <nav className="space-y-2">
              <button
                type="button"
                onClick={() => setActiveTab("users")}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeTab === "users"
                    ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                  />
                </svg>
                <span className="font-medium">จัดการผู้ใช้</span>
              </button>

              <button
                type="button"
                onClick={() => navigate("/admin/user-approval")}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="font-medium">อนุมัติผู้ใช้ใหม่</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("leave-types")}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeTab === "leave-types"
                    ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="font-medium">จัดการประเภทการลา</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("departments")}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeTab === "departments"
                    ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
                <span className="font-medium">จัดการแผนก</span>
              </button>
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {activeTab === "users"
                ? "การจัดการผู้ใช้"
                : activeTab === "leave-types"
                  ? "การจัดการประเภทการลา"
                  : "การจัดการแผนก"}
            </h2>
              <p className="text-gray-600 dark:text-gray-400">
                {activeTab === "users"
                  ? "จัดการข้อมูลผู้ใช้ในระบบ เปลี่ยนบทบาท สถานะ และตั้งรหัสผ่านใหม่"
                  : activeTab === "leave-types"
                    ? "จัดการประเภทการลาต่างๆ ในองค์กร"
                    : "จัดการข้อมูลแผนกและโครงสร้างองค์กร"}
              </p>
          </div>

          {/* Users Management Tab */}
          {activeTab === "users" && (
            <div>
              {loading ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-10">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600 dark:text-gray-400">
                      Loading admin panel...
                    </p>
                  </div>
                </div>
              ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-red-800">Error</h2>
                  <p className="text-red-600">{error}</p>
                  <button
                    type="button"
                    onClick={() => void loadUsersTabData()}
                    className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Reload Users
                  </button>
                </div>
              ) : (
                <>
              {/* Stats Cards */}
              {stats && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      Total Users
                    </h3>
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                      {Object.values(stats.users).reduce((a, b) => a + b, 0)}
                    </p>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                      Active: {stats.users.active || 0}
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      Roles
                    </h3>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Admin:</span>
                        <span className="font-semibold text-gray-900 dark:text-gray-100">
                          {stats.roles.admin || 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">HR:</span>
                        <span className="font-semibold text-gray-900 dark:text-gray-100">
                          {stats.roles.hr || 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Manager:</span>
                        <span className="font-semibold text-gray-900 dark:text-gray-100">
                          {stats.roles.manager || 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Employee:</span>
                        <span className="font-semibold text-gray-900 dark:text-gray-100">
                          {stats.roles.employee || 0}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      Departments
                    </h3>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {stats.departments}
                    </p>
                  </div>
                </div>
              )}

              {/* Filters */}
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 mb-6">
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-64">
                    <input
                      type="text"
                      placeholder="Search users..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      value={filters.search}
                      onChange={(e) =>
                        setFilters({ ...filters, search: e.target.value })
                      }
                    />
                  </div>

                  <select
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={filters.role}
                    onChange={(e) =>
                      setFilters({ ...filters, role: e.target.value })
                    }
                  >
                    <option value="">All Roles</option>
                    <option value="admin">Admin</option>
                    <option value="hr">HR</option>
                    <option value="manager">Manager</option>
                    <option value="employee">Employee</option>
                  </select>

                  <select
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={filters.status}
                    onChange={(e) =>
                      setFilters({ ...filters, status: e.target.value })
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="all">All Status</option>
                  </select>
                </div>
              </div>

              {/* Users Table */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Users</h2>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Department
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {users.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="font-medium text-gray-900 dark:text-gray-100">
                                {user.firstName} {user.lastName}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {user.email}
                              </div>
                              <div className="text-xs text-gray-400 dark:text-gray-500">
                                ID: {user.employeeId}
                              </div>
                            </div>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <select
                              className={`text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-600 ${
                                user.id === currentUser?.id ? "opacity-60" : ""
                              }`}
                              value={user.role}
                              onChange={(e) =>
                                updateUserRole(user.id, e.target.value)
                              }
                              disabled={
                                updatingUsers.has(user.id) ||
                                (user.id === currentUser?.id &&
                                  user.role === "admin")
                              }
                            >
                              <option value="employee">Employee</option>
                              <option value="manager">Manager</option>
                              <option value="hr">HR</option>
                              <option value="admin">Admin</option>
                            </select>
                            {updatingUsers.has(user.id) && (
                              <div className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                                Updating...
                              </div>
                            )}
                            {user.id === currentUser?.id &&
                              user.role === "admin" && (
                                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                  Cannot change own admin role
                                </div>
                              )}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            {user.department?.name || "No Department"}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <select
                              className={`text-xs font-semibold px-2 py-1 rounded-full border cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed ${
                                user.status === "active"
                                  ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700"
                                  : user.status === "inactive"
                                    ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700"
                                    : "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-300 dark:border-red-700"
                              } ${updatingUsers.has(user.id) || user.id === currentUser?.id ? "opacity-60" : ""}`}
                              value={user.status}
                              onChange={(e) =>
                                updateUserStatus(user.id, e.target.value)
                              }
                              disabled={
                                updatingUsers.has(user.id) ||
                                user.id === currentUser?.id
                              }
                            >
                              <option
                                value="active"
                                className="bg-white dark:bg-gray-800 text-black dark:text-white"
                              >
                                Active
                              </option>
                              <option
                                value="inactive"
                                className="bg-white dark:bg-gray-800 text-black dark:text-white"
                              >
                                Inactive
                              </option>
                              <option
                                value="terminated"
                                className="bg-white dark:bg-gray-800 text-black dark:text-white"
                              >
                                Terminated
                              </option>
                            </select>
                            {updatingUsers.has(user.id) && (
                              <div className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                                Updating...
                              </div>
                            )}
                            {user.id === currentUser?.id && (
                              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                Cannot change own status
                              </div>
                            )}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              type="button"
                              className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 mr-3"
                              onClick={() => viewUser(user)}
                            >
                              View
                            </button>
                            <button
                              type="button"
                              className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300"
                              onClick={() => editUser(user)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="ml-3 text-amber-600 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-300"
                              onClick={() => openResetPasswordModal(user)}
                              disabled={resettingPasswordUserId === user.id}
                            >
                              {resettingPasswordUserId === user.id
                                ? "Resetting..."
                                : "Reset Password"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                      {Math.min(
                        pagination.page * pagination.limit,
                        pagination.total
                      )}{" "}
                      of {pagination.total} results
                    </div>

                    <div className="flex space-x-2">
                      <button
                        type="button"
                        className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 disabled:opacity-50 bg-white dark:bg-gray-700"
                        disabled={pagination.page <= 1}
                        onClick={() =>
                          setPagination({
                            ...pagination,
                            page: pagination.page - 1,
                          })
                        }
                      >
                        Previous
                      </button>

                      <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                        {pagination.page} of {pagination.totalPages}
                      </span>

                      <button
                        type="button"
                        className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 disabled:opacity-50 bg-white dark:bg-gray-700"
                        disabled={pagination.page >= pagination.totalPages}
                        onClick={() =>
                          setPagination({
                            ...pagination,
                            page: pagination.page + 1,
                          })
                        }
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
                </>
              )}
            </div>
          )}

          {/* Leave Types Management Tab */}
          {activeTab === "leave-types" && <LeaveTypeManagement />}

          {/* Departments Management Tab */}
          {activeTab === "departments" && <DepartmentManagement />}
        </div>
      </div>

      {/* View User Modal */}
      {showViewModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  User Details
                </h2>
                <button
                  type="button"
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                    Personal Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Full Name</label>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {selectedUser.firstName} {selectedUser.lastName}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Email</label>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{selectedUser.email}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Phone Number
                      </label>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {selectedUser.phoneNumber || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                    Employment Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Employee ID
                      </label>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{selectedUser.employeeId}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Position</label>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {selectedUser.position || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Department
                      </label>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {selectedUser.department?.name || "No Department"}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Team</label>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {selectedUser.team?.name || "No Team"}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                    System Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Role</label>
                      <p className="font-medium text-gray-900 dark:text-gray-100 capitalize">
                        {selectedUser.role}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Status</label>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          selectedUser.status === "active"
                            ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                            : selectedUser.status === "inactive"
                              ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300"
                              : "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300"
                        }`}
                      >
                        {selectedUser.status}
                      </span>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Employment Type
                      </label>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {selectedUser.employmentType}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                    Dates
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Hire Date</label>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {new Date(selectedUser.hireDate).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Last Login
                      </label>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {selectedUser.lastLoginAt
                          ? new Date(selectedUser.lastLoginAt).toLocaleString()
                          : "Never"}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Created At
                      </label>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {new Date(selectedUser.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowViewModal(false);
                  editUser(selectedUser);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Edit User
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowViewModal(false);
                  openResetPasswordModal(selectedUser);
                }}
                className="px-4 py-2 bg-amber-500 text-white rounded-md hover:bg-amber-600"
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Edit User
                </h2>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                const departmentId = formData.get("departmentId") as string;
                const updateData = {
                  firstName: formData.get("firstName") as string,
                  lastName: formData.get("lastName") as string,
                  email: formData.get("email") as string,
                  employeeId: formData.get("employeeId") as string,
                  position: formData.get("position") as string,
                  phoneNumber: formData.get("phoneNumber") as string,
                  departmentId: departmentId || null,
                };
                updateUserInfo(editingUser.id, updateData);
              }}
              className="p-6 space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    defaultValue={editingUser.firstName}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    defaultValue={editingUser.lastName}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    defaultValue={editingUser.email}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Employee ID *
                  </label>
                  <input
                    type="text"
                    name="employeeId"
                    defaultValue={editingUser.employeeId}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Position
                  </label>
                  <input
                    type="text"
                    name="position"
                    defaultValue={editingUser.position || ""}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    name="phoneNumber"
                    defaultValue={editingUser.phoneNumber || ""}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    แผนก
                  </label>
                  <select
                    name="departmentId"
                    defaultValue={editingUser.department?.id || ""}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- ไม่มีแผนก --</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name} ({dept.code})
                      </option>
                    ))}
                  </select>
                  {editingUser.department?.id && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      แผนกปัจจุบัน: {editingUser.department.name}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && resetPasswordUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-xl w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    Reset Password
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    กำหนดรหัสผ่านใหม่ให้ {resetPasswordUser.firstName}{" "}
                    {resetPasswordUser.lastName}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeResetPasswordModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 text-sm text-amber-800 dark:text-amber-200">
                ระบบจะเปลี่ยนรหัสผ่านทันทีและบังคับให้ออกจาก session เดิมทั้งหมด
                {resetPasswordUser.id === currentUser?.id
                  ? " ยกเว้น session ปัจจุบันของคุณ"
                  : ""}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={resetPasswordForm.newPassword}
                  onChange={(e) =>
                    setResetPasswordForm((prev) => ({
                      ...prev,
                      newPassword: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="อย่างน้อย 8 ตัวอักษร"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  แนะนำให้มีตัวพิมพ์เล็ก พิมพ์ใหญ่ ตัวเลข และอักขระพิเศษ
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={resetPasswordForm.confirmPassword}
                  onChange={(e) =>
                    setResetPasswordForm((prev) => ({
                      ...prev,
                      confirmPassword: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="ยืนยันรหัสผ่านใหม่อีกครั้ง"
                />
              </div>

              {resetPasswordError && (
                <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                  {resetPasswordError}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeResetPasswordModal}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                disabled={resettingPasswordUserId === resetPasswordUser.id}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={resetUserPassword}
                className="px-4 py-2 bg-amber-500 text-white rounded-md hover:bg-amber-600 disabled:opacity-60"
                disabled={resettingPasswordUserId === resetPasswordUser.id}
              >
                {resettingPasswordUserId === resetPasswordUser.id
                  ? "Saving..."
                  : "Set New Password"}
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default AdminPanel;
